import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

// Define tab type to avoid inline generics which can conflict with build tools
type ExploreTab = "featured" | "all-funds" | "all-stocks";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FundCard } from "@/components/marketplace/funds/FundCard";
import { LoadingState } from "@/components/marketplace/funds/LoadingState";
import { ErrorState } from "@/components/marketplace/funds/ErrorState";
import { EmptyState } from "@/components/marketplace/funds/EmptyState";
import FundsRawExplorer from "@/components/funds-raw/FundsRawExplorer";
import StocksRawExplorer from "@/components/stocks-raw/StocksRawExplorer";
import ExploreTabsHeader from "@/components/explore/ExploreTabsHeader";
import { MobileViewportWarning } from "@/components/ui/MobileViewportWarning";
import { useIsMobile } from "@/hooks/use-mobile";
import { isDebugEnabled } from "@/utils/debug";
import { PageContainer } from "@/components/ui/PageContainer";
type FundPerformanceRow = {
  date: string;
  performance_value: number;
  metric_name: string;
  frequency?: string | null;
};

type FundRow = {
  id: string;
  fund_name: string;
  fund_performance?: FundPerformanceRow[];
};

const ExploreOffers = () => {
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  
  // State to track which tab is active
  const [activeTab, setActiveTab] = useState<"featured" | "all-funds" | "all-stocks">("featured");
  
  // Set initial tab based on URL parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'all-stocks' || tabParam === 'all-funds' || tabParam === 'featured') {
      setActiveTab(tabParam);
    }
  }, [searchParams]);
  
  // Query for featured funds with optimized caching
  const { data: funds, isLoading, error } = useQuery<FundRow[]>({
    queryKey: ['approved-funds'],
    queryFn: async () => {
      if (isDebugEnabled()) console.log('Fetching approved funds');
      
      const { data, error } = await (supabase as any)
        .from('funds')
        .select(`
          *,
          fund_performance (
            date,
            performance_value,
            metric_name,
            frequency
          )
        `)
        .eq('is_approved', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase query error:', error);
        toast.error('Failed to load funds');
        throw error;
      }
      
      if (isDebugEnabled()) console.log('Fetched approved funds:', data);
      // Cast to expected shape for rendering; runtime data remains unchanged
      return (data ?? []) as unknown as FundRow[];
    },
    // Only fetch when the featured tab is active
    enabled: activeTab === "featured",
    // Optimize caching to prevent refetching
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const renderFundCards = useMemo(() => {
    if (!funds) return null;

    return (funds || []).map((fund: FundRow, index: number) => {
      const currentYear = new Date().getFullYear();
      
      if (isDebugEnabled()) console.log(`All performance records for ${fund.fund_name}:`, fund.fund_performance);
      
      const currentYearYTD = (fund.fund_performance || [])
        .filter((p: FundPerformanceRow) => {
          const performanceYear = new Date(p.date).getFullYear();
          const isCurrentYear = performanceYear === currentYear;
          const isYTD = p.frequency?.toLowerCase() === 'ytd';
          const isReturn = p.metric_name === 'return';

          return isReturn && isCurrentYear && isYTD;
        })
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];

      const ytdPerformance = currentYearYTD || (fund.fund_performance || [])
        .filter((p: FundPerformanceRow) => {
          const isYTD = p.frequency?.toLowerCase() === 'ytd';
          const isReturn = p.metric_name === 'return';
          return isReturn && isYTD;
        })
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];

      if (isDebugEnabled()) console.log(`YTD Performance found for ${fund.fund_name}:`, ytdPerformance);

      return (
        <FundCard
          key={fund.id}
          fund={fund}
          ytdPerformance={ytdPerformance}
          priority={index < 3}
        />
      );
    });
  }, [funds]);

  return (
    <div className="flex-1">
      <SidebarInset>
        <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4 sticky top-0 bg-background z-10">
          <SidebarTrigger className="-ml-2" />
          <Separator orientation="vertical" className="h-6" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Dashboard</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator>
                <ChevronRight className="h-4 w-4" />
              </BreadcrumbSeparator>
              <BreadcrumbItem>
                <BreadcrumbPage>Discover</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </header>

        <PageContainer maxWidthClassName="max-w-[960px]">
          {/* Title, subtitle, and tabs area */}
          <div className="flex flex-col mb-2">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">Discover</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {activeTab === "featured" 
                    ? "Explore our curated selection of high-quality investment funds" 
                    : activeTab === "all-funds"
                    ? "Browse and filter the complete catalog of available funds"
                    : "Discover and analyze individual stocks from our database"}
                </p>
              </div>
            </div>
            
            {/* Mobile warning for complex table views */}
            {isMobile && (activeTab === "all-funds" || activeTab === "all-stocks") && (
              <div className="mt-4">
                <MobileViewportWarning 
                  message="For the best experience with filters and tables, use a larger screen or rotate your device to landscape."
                  hideTableOnMobile={true}
                />
              </div>
            )}
            
            {/* Tabs below title and subtitle */}
            <div className="mt-6">
              <ExploreTabsHeader
                tabs={[
                  { id: "featured" as ExploreTab, label: "Featured Funds" },
                  { id: "all-funds" as ExploreTab, label: "All Funds" },
                  { id: "all-stocks" as ExploreTab, label: "All Stocks" }
                ]}
                activeTab={activeTab}
                onTabChange={(tabId: ExploreTab) => setActiveTab(tabId)}
              />
            </div>
          </div>

          {/* Conditional rendering based on active tab */}
          {activeTab === "featured" ? (
            <div>
              {isLoading ? (
                <LoadingState />
              ) : error ? (
                <ErrorState />
              ) : !(funds?.length || 0) ? (
                <EmptyState />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {renderFundCards}
                </div>
              )}
            </div>
          ) : activeTab === "all-funds" ? (
            // All Funds view with FundsRawExplorer
            <FundsRawExplorer />
          ) : (
            // All Stocks view with StocksRawExplorer
            <StocksRawExplorer />
          )}
        </PageContainer>
      </SidebarInset>
    </div>
  );
};

export default ExploreOffers;
